/**
 * Zoom utility functions for chart date range manipulation
 */
import moment from 'moment'

/**
 * Zoom level definition (in minutes)
 */
interface ZoomLevel {
  minutes: number
  label: string
}

/**
 * Complete list of zoom levels from smallest to largest
 * Includes presets from TimeRange enum plus additional levels for smooth zooming
 */
const ZOOM_LEVELS: ZoomLevel[] = [
  { minutes: 1 / 60, label: '1s' }, // 1 second
  { minutes: 10 / 60, label: '10s' }, // 10 seconds
  { minutes: 30 / 60, label: '30s' }, // 30 seconds
  { minutes: 1, label: '1m' }, // 1 minute
  { minutes: 5, label: '5m' }, // 5 minutes
  { minutes: 10, label: '10m' }, // TimeRange.TEN_MIN
  { minutes: 30, label: '30m' }, // TimeRange.THIRTY_MIN
  { minutes: 60, label: '1h' }, // TimeRange.ONE_HOUR
  { minutes: 180, label: '3h' }, // TimeRange.THREE_HOURS
  { minutes: 360, label: '6h' }, // TimeRange.SIX_HOURS
  { minutes: 720, label: '12h' }, // TimeRange.TWELVE_HOURS
  { minutes: 1440, label: '1d' }, // TimeRange.ONE_DAY
  { minutes: 2880, label: '2d' }, // TimeRange.TWO_DAYS
  { minutes: 4320, label: '3d' }, // TimeRange.THREE_DAYS
  { minutes: 10080, label: '7d' }, // TimeRange.SEVEN_DAYS
  { minutes: 20160, label: '14d' }, // 14 days
  { minutes: 43200, label: '30d' }, // 30 days
  { minutes: 86400, label: '60d' }, // 60 days
  { minutes: 129600, label: '90d' }, // 90 days
]

/**
 * Get the complete list of zoom levels
 */
export const getZoomLevels = (): ZoomLevel[] => {
  return ZOOM_LEVELS
}

/**
 * Get minimum zoom level (smallest interval)
 */
export const getMinZoomLevel = (): ZoomLevel => {
  return ZOOM_LEVELS[0]
}

/**
 * Get maximum zoom level (largest interval)
 */
export const getMaxZoomLevel = (): ZoomLevel => {
  return ZOOM_LEVELS[ZOOM_LEVELS.length - 1]
}

/**
 * Calculate interval in minutes from ISO date strings
 */
export const calculateIntervalMinutes = (
  startDate: string,
  endDate: string,
): number => {
  const start = moment(startDate)
  const end = moment(endDate)
  return end.diff(start, 'minutes', true) // true = floating point
}

/**
 * Find the closest zoom level for a given interval in minutes
 * @param intervalMinutes - The interval in minutes
 * @returns The closest zoom level
 */
export const findClosestZoomLevel = (intervalMinutes: number): ZoomLevel => {
  let closest = ZOOM_LEVELS[0]
  let minDiff = Math.abs(intervalMinutes - closest.minutes)

  for (const level of ZOOM_LEVELS) {
    const diff = Math.abs(intervalMinutes - level.minutes)
    if (diff < minDiff) {
      minDiff = diff
      closest = level
    }
  }

  return closest
}

/**
 * Find the exact zoom level match or null if not found
 */
export const findExactZoomLevel = (
  intervalMinutes: number,
): ZoomLevel | null => {
  // Allow small tolerance for floating point comparison (0.1%)
  const tolerance = intervalMinutes * 0.001

  for (const level of ZOOM_LEVELS) {
    if (Math.abs(intervalMinutes - level.minutes) <= tolerance) {
      return level
    }
  }

  return null
}

/**
 * Zoom out - get the next larger interval
 * @param currentIntervalMinutes - Current interval in minutes
 * @returns New interval in minutes after zoom out, or null if already at max
 */
export const zoomOut = (currentIntervalMinutes: number): number | null => {
  // Find current level (exact or closest)
  const exactLevel = findExactZoomLevel(currentIntervalMinutes)
  const currentLevel =
    exactLevel || findClosestZoomLevel(currentIntervalMinutes)

  // Find current index
  const currentIndex = ZOOM_LEVELS.findIndex(
    (level) => level.minutes === currentLevel.minutes,
  )

  // If we're not on an exact level and the current interval is larger than the closest,
  // return the next level up from closest
  if (!exactLevel && currentIntervalMinutes > currentLevel.minutes) {
    const nextIndex = currentIndex + 1
    if (nextIndex < ZOOM_LEVELS.length) {
      return ZOOM_LEVELS[nextIndex].minutes
    }
    return null // Already at max
  }

  // Move to next level
  const nextIndex = currentIndex + 1
  if (nextIndex >= ZOOM_LEVELS.length) {
    return null // Already at max zoom out
  }

  return ZOOM_LEVELS[nextIndex].minutes
}

/**
 * Zoom in - get the next smaller interval
 * @param currentIntervalMinutes - Current interval in minutes
 * @returns New interval in minutes after zoom in, or null if already at min
 */
export const zoomIn = (currentIntervalMinutes: number): number | null => {
  // Find current level (exact or closest)
  const exactLevel = findExactZoomLevel(currentIntervalMinutes)
  const currentLevel =
    exactLevel || findClosestZoomLevel(currentIntervalMinutes)

  // Find current index
  const currentIndex = ZOOM_LEVELS.findIndex(
    (level) => level.minutes === currentLevel.minutes,
  )

  // If we're not on an exact level and the current interval is smaller than the closest,
  // return the next level down from closest
  if (!exactLevel && currentIntervalMinutes < currentLevel.minutes) {
    const nextIndex = currentIndex - 1
    if (nextIndex >= 0) {
      return ZOOM_LEVELS[nextIndex].minutes
    }
    return null // Already at min
  }

  // Move to previous level
  const nextIndex = currentIndex - 1
  if (nextIndex < 0) {
    return null // Already at max zoom in
  }

  return ZOOM_LEVELS[nextIndex].minutes
}

/**
 * Check if can zoom out from current interval
 */
export const canZoomOut = (currentIntervalMinutes: number): boolean => {
  return zoomOut(currentIntervalMinutes) !== null
}

/**
 * Check if can zoom in from current interval
 */
export const canZoomIn = (currentIntervalMinutes: number): boolean => {
  return zoomIn(currentIntervalMinutes) !== null
}

/**
 * Calculate new start date for zoom operation, keeping end date fixed
 * @param endDate - Fixed end date (ISO string)
 * @param newIntervalMinutes - New interval in minutes
 * @returns New start date (ISO string)
 */
export const calculateNewStartDate = (
  endDate: string,
  newIntervalMinutes: number,
): string => {
  const end = moment(endDate)
  const newStart = end.clone().subtract(newIntervalMinutes, 'minutes')
  return newStart.toISOString()
}
